import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Combobox } from 'medere-ui-framework';
import {
  AgendaProfessional,
  ComboOption,
  ScheduleSetup,
} from '../types/agenda.types';
import {
  getAgendaProfessionalsCombo,
  getScheduleSetups,
  getAvailableLocations,
  changeSetupLocation,
} from '../services/AgendaService';
import { parseApiError } from '../types/error.types';
import { FieldError } from '../../../components/novarium/NvTableParts';

interface ChangeLocationModalProps {
  visible: boolean;
  onHide: () => void;
  onConfirmed: () => void;
  onError: (message: string) => void;
  initialProfessional?: AgendaProfessional | null;
  initialSetupId?: number | null;
}

interface ValidationErrors {
  setups?: string;
  location?: string;
}

export default function ChangeLocationModal({
  visible,
  onHide,
  onConfirmed,
  onError,
  initialProfessional,
  initialSetupId,
}: ChangeLocationModalProps) {
  const [professional, setProfessional] = useState<AgendaProfessional | null>(null);
  const [setups, setSetups] = useState<ScheduleSetup[]>([]);
  const [selectedSetups, setSelectedSetups] = useState<ScheduleSetup[]>([]);
  const [locations, setLocations] = useState<ComboOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [setupsLoaded, setSetupsLoaded] = useState(false);
  const [loadingSetups, setLoadingSetups] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const todayLabel = useMemo(() => {
    const now = new Date();
    return format(now, 'd/M/yyyy', { locale: es });
  }, []);

  // Initialize with pre-selected professional when modal opens
  useEffect(() => {
    if (visible) {
      const prof = initialProfessional ?? null;
      setProfessional(prof);
      setSetups([]);
      setSelectedSetups([]);
      setLocations([]);
      setSelectedLocationId(null);
      setSetupsLoaded(false);
      setErrors({});

      // Auto-load setups if professional is pre-selected
      if (prof) {
        loadSetups(prof.id);
      }
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Los errores generales (carga y guardado) van a la card flotante de la vista, por onError
  const loadSetups = useCallback(
    async (professionalId: number) => {
      setLoadingSetups(true);
      try {
        const data = await getScheduleSetups(professionalId);
        setSetups(data);
        setSetupsLoaded(true);

        // Auto-select the initial setup if provided
        if (initialSetupId) {
          const match = data.filter((s) => s.setupId === initialSetupId);
          if (match.length > 0) {
            setSelectedSetups(match);
          }
        }
      } catch (err) {
        onError(parseApiError(err, 'No se pudieron cargar las agendas del profesional. Intentá de nuevo.'));
        setSetups([]);
        setSetupsLoaded(false);
      } finally {
        setLoadingSetups(false);
      }
    },
    [initialSetupId, onError],
  );

  // Load available locations when selection changes
  useEffect(() => {
    if (selectedSetups.length === 0) {
      setLocations([]);
      setSelectedLocationId(null);
      return;
    }

    const setupIds = selectedSetups.map((s) => s.setupId);
    setLoadingLocations(true);
    getAvailableLocations(setupIds)
      .then((data) => {
        setLocations(data);
        // Reset location selection since available options changed
        setSelectedLocationId(null);
      })
      .catch((err) => {
        onError(parseApiError(err, 'No se pudieron cargar los consultorios disponibles. Intentá de nuevo.'));
        setLocations([]);
        setSelectedLocationId(null);
      })
      .finally(() => setLoadingLocations(false));
  }, [selectedSetups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProfessionalChange = useCallback((prof: AgendaProfessional | null) => {
    setProfessional(prof);
    // Reset and auto-load setups when professional changes
    setSelectedSetups([]);
    setLocations([]);
    setSelectedLocationId(null);
    setErrors({});

    if (prof) {
      loadSetups(prof.id);
    } else {
      setSetups([]);
      setSetupsLoaded(false);
    }
  }, [loadSetups]);

  // "Cambiar consultorio" siempre habilitado: si falta elegir algo, se marca y no se envía
  const handleConfirm = useCallback(async () => {
    const validation: ValidationErrors = {
      setups: professional === null || selectedSetups.length === 0 ? 'Elegí al menos una agenda' : undefined,
      // Sin agenda elegida el consultorio está deshabilitado ("Elegí una agenda primero"): se marca solo la agenda
      location: selectedSetups.length > 0 && selectedLocationId === null ? 'Elegí el consultorio' : undefined,
    };
    if (validation.setups || validation.location) {
      setErrors(validation);
      return;
    }

    setSubmitting(true);
    try {
      await changeSetupLocation({
        locationId: selectedLocationId!,
        setupIds: selectedSetups.map((s) => s.setupId),
      });
      onConfirmed();
      onHide();
    } catch (err) {
      onError(parseApiError(err, 'No se pudo cambiar el consultorio. Intentá de nuevo.'));
    } finally {
      setSubmitting(false);
    }
  }, [professional, selectedLocationId, selectedSetups, onConfirmed, onHide, onError]);

  const footer = (
    <>
      <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} disabled={submitting} />
      <Button type="button" className="buttonMain" label="Cambiar consultorio" onClick={handleConfirm} loading={submitting} />
    </>
  );

  const locationDisabled = selectedSetups.length === 0 || loadingLocations;

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header="Cambiar consultorio"
      footer={footer}
      modal
      className="nv-scope nv-dialog"
      style={{ width: '90vw', maxWidth: '780px' }}
      closable
      draggable={false}
    >
      <div className="nv-dialog-body">
        <p className="nv-note">
          <i className="pi pi-info-circle" aria-hidden /> Agendas del día {todayLabel}
        </p>

        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="change-location-professional">Profesional</label>
            {/* TODO(design-system): el Combobox de medere-ui-framework no acepta panelClassName (panel sin nv-scope). */}
            <Combobox
              inputId="change-location-professional"
              getItems={getAgendaProfessionalsCombo}
              optionLabel="name"
              value={professional}
              setValue={handleProfessionalChange}
              placeholder="Seleccionar"
              minPanelWidth={350}
            />
          </div>
        </div>

        <div className="nv-stack-4">
          <DataTable
            value={setups}
            selection={selectedSetups}
            onSelectionChange={(e) => {
              setSelectedSetups(e.value as ScheduleSetup[]);
              setErrors((current) => ({ ...current, setups: undefined }));
            }}
            dataKey="setupId"
            selectionMode="checkbox"
            loading={loadingSetups}
            emptyMessage={
              setupsLoaded
                ? 'El profesional no tiene agendas hoy.'
                : 'Elegí un profesional para ver sus agendas.'
            }
            scrollable
            scrollHeight="280px"
            className="nv-compact"
            aria-describedby={errors.setups ? 'change-location-setups-error' : undefined}
          >
            <Column selectionMode="multiple" headerClassName="nv-col-check" className="nv-col-check" />
            <Column field="professional" header="Profesional" />
            <Column field="description" header="Descripción" />
            <Column field="activityType" header="Actividad" />
            <Column field="location" header="Consultorio/Sala/Box" />
            <Column
              field="appointments"
              header="Turnos"
              align="center"
              alignHeader="center"
              headerClassName="nv-col-qty"
              className="nv-col-qty"
            />
          </DataTable>
          <FieldError id="change-location-setups-error" message={errors.setups} />
        </div>

        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="change-location-location">Consultorio/Sala/Box <span className="nv-req">*</span></label>
            <Dropdown
              inputId="change-location-location"
              value={selectedLocationId}
              options={locations}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => {
                setSelectedLocationId(e.value);
                if (e.value != null) setErrors((current) => ({ ...current, location: undefined }));
              }}
              placeholder={selectedSetups.length === 0 ? 'Elegí una agenda primero' : 'Seleccionar'}
              disabled={locationDisabled}
              className={errors.location ? 'p-invalid' : undefined}
              aria-describedby={errors.location ? 'change-location-location-error' : undefined}
              panelClassName="nv-scope"
              showClear
            />
            <FieldError id="change-location-location-error" message={errors.location} />
          </div>
        </div>
      </div>
    </Dialog>
  );
}
